/**
 * PDF « Statistiques de délibération » : document sobre destiné à la direction
 * et à l'académie. Une seule couleur d'accent, tableaux à filets fins.
 *
 * Cohérence des chiffres (mêmes règles que l'écran) :
 *  - inscrits = validés (ordinaire + rattrapage) + ajournés + sans résultat ;
 *  - parts d'un même total arrondies par plus forts restes (somme = 100 %) ;
 *  - taux de la session ordinaire sur les inscrits, du rattrapage sur les concernés.
 */
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { StatsDeliberationResponse, BreakdownDeliberation } from '../../services/resultatsApi';

export type TypeFormationStats = 'FI_DD' | 'FI' | 'DD' | 'CS' | 'TOUS';
type RGB = [number, number, number];

// ─── Formats ────────────────────────────────────────────────────────────────

/** Une décimale ; une part non nulle qui s'arrondirait à 0,0 % s'affiche « < 0.1% ». */
export const fmtPctVal = (p: number): string =>
  (p > 0 && p < 0.05) ? '< 0.1%' : `${p.toFixed(1)}%`;
export const fmtPct = (n: number | undefined | null): string =>
  (n === undefined || n === null) ? '—' : fmtPctVal(n);
export const pctDe = (n: number, total: number): string =>
  total > 0 ? fmtPctVal((n * 100) / total) : '—';

/** Parts d'un même total arrondies à 0,1 % par la méthode des plus forts restes :
 *  quand les valeurs couvrent le total, les pourcentages affichés font exactement 100 %. */
export const repartir = (valeurs: number[], total: number): string[] => {
  if (total <= 0) return valeurs.map(() => '—');
  const bruts = valeurs.map(v => (v * 1000) / total);
  const unites = bruts.map(b => Math.floor(b));
  const cible = Math.round(bruts.reduce((s, b) => s + b, 0));
  const ordre = bruts.map((b, i) => ({ reste: b - unites[i], i })).sort((a, b) => b.reste - a.reste);
  for (let k = 0; k < cible - unites.reduce((s, u) => s + u, 0) && k < ordre.length; k++) {
    unites[ordre[k].i] += 1;
  }
  return unites.map((u, i) => (valeurs[i] > 0 && u === 0 ? '< 0.1%' : `${(u / 10).toFixed(1)}%`));
};

const fmtInt = (n: number | undefined | null): string =>
  (n === undefined || n === null) ? '-' : String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

/** Nettoie un texte pour les polices standard de jsPDF (jeu WinAnsi). */
export function safeTxt(s: string | number | undefined | null): string {
  if (s === undefined || s === null) return '';
  return String(s)
    .replace(/→/g, '->').replace(/÷/g, '/').replace(/×/g, 'x')
    .replace(/…/g, '...').replace(/[“”«»]/g, '"').replace(/[‘’]/g, "'")
    .replace(/—/g, '-').replace(/–/g, '-').replace(/≥/g, '>=').replace(/≤/g, '<=')
    .replace(/[\u00a0\u202f]/g, ' ');
}

async function chargerLogoBase64(src: string): Promise<string | null> {
  try {
    const res = await fetch(src);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch { return null; }
}

// ─── Charte ─────────────────────────────────────────────────────────────────

const ENCRE: RGB = [30, 41, 59];          // texte principal
const GRIS: RGB = [100, 116, 139];        // texte secondaire
const FILET: RGB = [203, 213, 225];       // traits de tableau
const FOND_ENTETE: RGB = [241, 245, 249]; // en-têtes de tableau
const ACCENT: RGB = [30, 58, 95];         // bleu nuit : titres de section uniquement
const BARRE: RGB = [100, 116, 139];       // mini-barres de répartition

const LIB_FORMATION: Record<TypeFormationStats, string> = {
  FI_DD: 'Formation initiale et double diplôme (cours du soir exclus)',
  FI: 'Formation initiale (cours du soir exclus)',
  DD: 'Double diplôme',
  CS: 'Cours du soir',
  TOUS: 'Toutes formations (FI, DD et cours du soir)',
};
const MENTIONS: Array<[string, string]> = [
  ['tres_bien', 'Très bien'], ['bien', 'Bien'], ['assez_bien', 'Assez bien'],
  ['passable', 'Passable'], ['ajourne', 'Ajourné'],
];

export interface OptionsPdfStats {
  datasets: Array<{ sem: 1 | 2; stats: StatsDeliberationResponse | null }>;
  annee?: string;
  perimetre: { pole?: string; niveau?: string; option?: string; classe?: string };
  typeFormation: TypeFormationStats;
  inclureOrdinaire: boolean;
  inclureRattrapage: boolean;
  inclureRachat: boolean;
  seuilRachat: number;
  institut?: { ville?: string; signataireTitre?: string; signataireNom?: string };
  nomFichier: string;
}

export async function genererPdfStatsDeliberation(o: OptionsPdfStats): Promise<void> {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const MX = 18;
  const LARG = W - 2 * MX;
  const HAUT_SUITE = 22; // début du contenu sur les pages suivantes
  const BAS = H - 22;    // limite basse du contenu (pied de page en dessous)
  const annee = o.annee || o.datasets.find(d => d.stats)?.stats?.annee || '';
  const dateFr = new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

  const texte = (
    t: string, x: number, yy: number,
    opt: { taille?: number; gras?: boolean; italique?: boolean; couleur?: RGB; align?: 'left' | 'center' | 'right' } = {},
  ) => {
    const style = opt.gras ? (opt.italique ? 'bolditalic' : 'bold') : (opt.italique ? 'italic' : 'normal');
    doc.setFont('helvetica', style);
    doc.setFontSize(opt.taille ?? 9);
    doc.setTextColor(...(opt.couleur ?? ENCRE));
    doc.text(safeTxt(t), x, yy, { align: opt.align ?? 'left' });
  };
  const filet = (yy: number, epaisseur = 0.2, couleur: RGB = FILET, x1 = MX, x2 = W - MX) => {
    doc.setDrawColor(...couleur);
    doc.setLineWidth(epaisseur);
    doc.line(x1, yy, x2, yy);
  };
  const finTableau = (): number => (doc as any).lastAutoTable.finalY;

  let y = 0;
  const assurer = (besoin: number) => {
    if (y + besoin > BAS) { doc.addPage(); y = HAUT_SUITE; }
  };
  const note = (t: string) => {
    const lignes = doc.splitTextToSize(safeTxt(t), LARG);
    assurer(lignes.length * 3.4 + 2);
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(7.5);
    doc.setTextColor(...GRIS);
    doc.text(lignes, MX, y);
    y += lignes.length * 3.4 + 3;
  };
  let numSection = 0;
  const titreSection = (t: string) => {
    assurer(24);
    numSection += 1;
    texte(`${numSection}. ${t}`, MX, y, { taille: 10.5, gras: true, couleur: ACCENT });
    filet(y + 2, 0.25);
    y += 6.5;
  };

  const baseTable = {
    theme: 'grid' as const,
    margin: { left: MX, right: MX, top: HAUT_SUITE, bottom: H - BAS },
    styles: {
      font: 'helvetica', fontSize: 8, textColor: ENCRE, lineColor: FILET,
      lineWidth: 0.15, cellPadding: 1.6, valign: 'middle' as const,
    },
    headStyles: { fillColor: FOND_ENTETE, textColor: ENCRE, fontStyle: 'bold' as const, halign: 'center' as const },
    footStyles: { fillColor: FOND_ENTETE, textColor: ENCRE, fontStyle: 'bold' as const },
    bodyStyles: { fillColor: [255, 255, 255] as RGB },
  };

  // ─── En-tête de la première page ──────────────────────────────────────────
  const logo = await chargerLogoBase64('/logo.png');
  if (logo) {
    try { doc.addImage(logo, 'PNG', MX, 10, 15, 15); } catch { /* logo facultatif */ }
  }
  const xId = logo ? MX + 19 : MX;
  texte('INSTITUT INTERNATIONAL 2i', xId, 16, { taille: 11, gras: true });
  texte('Instruire pour Mieux Servir', xId, 21, { taille: 8.5, italique: true, couleur: GRIS });
  if (annee) texte(`Année académique ${annee}`, W - MX, 16, { taille: 8.5, couleur: GRIS, align: 'right' });
  texte(`Édité le ${dateFr}`, W - MX, 21, { taille: 8.5, couleur: GRIS, align: 'right' });
  filet(28, 0.5, ENCRE);

  texte('STATISTIQUES DE DÉLIBÉRATION', W / 2, 40, { taille: 14, gras: true, align: 'center' });
  const libSem = o.datasets.map(d => (d.sem === 1 ? 'semestre impair (S1, S3, S5)' : 'semestre pair (S2, S4, S6)')).join(' et ');
  texte(libSem.charAt(0).toUpperCase() + libSem.slice(1), W / 2, 46, { taille: 9.5, couleur: GRIS, align: 'center' });

  const { pole, niveau, option, classe } = o.perimetre;
  const sessions = [
    o.inclureOrdinaire ? 'session ordinaire' : '',
    o.inclureRattrapage ? 'session de rattrapage' : '',
    o.inclureRachat ? `simulation de rachat (seuil ${o.seuilRachat.toFixed(2)} / 20)` : '',
  ].filter(Boolean);
  const perimetre = [
    ['Périmètre', [
      pole ? `Pôle ${pole}` : 'Tous les pôles',
      niveau ? `Niveau ${niveau}` : 'Tous les niveaux',
      option ? `Option ${option}` : '',
      classe ? `Classe ${classe}` : '',
    ].filter(Boolean).join(' · ')],
    ['Formation', LIB_FORMATION[o.typeFormation]],
    ['Contenu', sessions.length ? sessions.join(', ') : 'synthèse et détails'],
  ];
  autoTable(doc, {
    startY: 53,
    body: perimetre.map(r => r.map(safeTxt)),
    theme: 'plain',
    margin: { left: MX, right: MX },
    styles: { font: 'helvetica', fontSize: 8.5, textColor: ENCRE, cellPadding: { top: 0.7, bottom: 0.7, left: 0, right: 2 } },
    columnStyles: { 0: { cellWidth: 28, textColor: GRIS } },
  });
  y = finTableau() + 7;

  // ─── Un bloc par semestre ─────────────────────────────────────────────────
  for (const { sem, stats } of o.datasets) {
    if (o.datasets.length > 1) {
      assurer(30);
      texte(sem === 1 ? 'Semestre impair (S1, S3, S5)' : 'Semestre pair (S2, S4, S6)', MX, y, { taille: 12, gras: true });
      filet(y + 2.2, 0.5, ENCRE);
      y += 9;
      numSection = 0;
    }
    if (!stats || stats.effectif_inscrits === 0) {
      note('Aucun étudiant dans ce périmètre pour ce semestre.');
      continue;
    }

    const so = stats.session_ordinaire;
    const sr = stats.session_rattrapage;
    const inscrits = stats.effectif_inscrits;
    const admisOrd = so?.nb_admis ?? 0;
    const admisRatt = sr?.nb_admis ?? 0;
    const valides = stats.nb_admis_total ?? (admisOrd + admisRatt);
    const ajournes = stats.nb_ajournes_final ?? Math.max((so?.nb_ajournes ?? 0) - admisRatt, 0);
    const sansRes = stats.nb_sans_resultat ?? Math.max(inscrits - valides - ajournes, 0);
    const [pValides, pAjournes, pSansRes] = repartir([valides, ajournes, sansRes], inscrits);
    const sup14 = stats.nb_sup14 ?? 0;
    const sup16 = stats.nb_sup16 ?? 0;

    // 1. Synthèse
    titreSection('Synthèse des résultats');
    const LIGNES_DONT = [2, 3];
    const LIGNES_FORTES = [0, 1, 4, 5];
    autoTable(doc, {
      ...baseTable,
      startY: y,
      head: [['Indicateur', 'Effectif', 'Part des inscrits']],
      body: [
        // La colonne donne la part DES inscrits : sur la ligne des inscrits
        // eux-memes elle vaut 100 % par construction. L'afficher laissait
        // croire a un taux de participation de 100 % ; le vrai taux est au
        // tableau 2 (« Participation et admission par session »).
        ['Étudiants inscrits', fmtInt(inscrits), ''],
        ['Validés', fmtInt(valides), pValides],
        ['dont admis en session ordinaire', fmtInt(admisOrd), pctDe(admisOrd, inscrits)],
        ['dont admis après rattrapage', fmtInt(admisRatt), pctDe(admisRatt, inscrits)],
        ['Ajournés', fmtInt(ajournes), pAjournes],
        ['Sans résultat (semestre non calculé)', fmtInt(sansRes), pSansRes],
      ].map(r => r.map(safeTxt)),
      columnStyles: { 0: { halign: 'left' }, 1: { halign: 'right', cellWidth: 32 }, 2: { halign: 'right', cellWidth: 38 } },
      didParseCell: (d) => {
        if (d.section === 'head' && d.column.index === 0) d.cell.styles.halign = 'left';
        if (d.section !== 'body') return;
        if (LIGNES_FORTES.includes(d.row.index)) d.cell.styles.fontStyle = 'bold';
        if (LIGNES_DONT.includes(d.row.index)) {
          d.cell.styles.textColor = GRIS;
          if (d.column.index === 0) d.cell.styles.cellPadding = { top: 1.6, bottom: 1.6, left: 7, right: 1.6 };
        }
      },
    });
    y = finTableau() + 2;
    autoTable(doc, {
      ...baseTable,
      startY: y,
      body: [
        ['Moyenne générale des résultats calculés', `${(stats.moyenne_generale ?? 0).toFixed(2)} / 20`, ''],
        ["Mention Bien et plus (moyenne d'au moins 14)", fmtInt(sup14), pctDe(sup14, inscrits)],
        ["Mention Très bien (moyenne d'au moins 16)", fmtInt(sup16), pctDe(sup16, inscrits)],
      ].map(r => r.map(safeTxt)),
      columnStyles: { 0: { halign: 'left' }, 1: { halign: 'right', cellWidth: 32 }, 2: { halign: 'right', cellWidth: 38 } },
    });
    y = finTableau() + 3;
    note("Inscrits = validés + ajournés + sans résultat. Les parts sont calculées sur l'effectif inscrit ; "
      + 'la moyenne générale porte sur les seuls résultats calculés.');

    // 2. Participation et admission par session
    if (o.inclureOrdinaire || o.inclureRattrapage) {
      titreSection('Participation et admission par session');
      const col = (ord: string, ratt: string) => [
        ...(o.inclureOrdinaire ? [ord] : []),
        ...(o.inclureRattrapage ? [ratt] : []),
      ];
      autoTable(doc, {
        ...baseTable,
        startY: y,
        head: [['Indicateur', ...col('Session ordinaire', 'Session de rattrapage')]],
        body: [
          ['Étudiants concernés', ...col(fmtInt(inscrits), fmtInt(sr?.nb_concernes))],
          ['Présents', ...col(fmtInt(so?.nb_participants), fmtInt(sr?.nb_participants))],
          ['Absents', ...col(fmtInt(so?.nb_absents), fmtInt(sr?.nb_absents))],
          ['Taux de participation', ...col(fmtPct(so?.taux_participation), fmtPct(sr?.taux_participation))],
          ['Admis', ...col(fmtInt(admisOrd), fmtInt(admisRatt))],
          ["Taux d'admission", ...col(fmtPct(so?.taux_admission), fmtPct(sr?.taux_admission))],
          ["Ajournés à l'issue de la session", ...col(fmtInt(so?.nb_ajournes), fmtInt(ajournes))],
          ['Sans résultat (semestre non calculé)', ...col(fmtInt(sansRes), '-')],
        ].map(r => r.map(safeTxt)),
        columnStyles: { 0: { halign: 'left' }, 1: { halign: 'right', cellWidth: 40 }, 2: { halign: 'right', cellWidth: 40 } },
        didParseCell: (d) => {
          if (d.section === 'head' && d.column.index === 0) d.cell.styles.halign = 'left';
          if (d.section === 'body' && [3, 5].includes(d.row.index)) d.cell.styles.fontStyle = 'bold';
        },
      });
      y = finTableau() + 3;
      note("Présent en session ordinaire : au moins 40 % de ses matières d'examen composées (copie associée "
        + "ou note d'examen supérieure à 0), TP exclus ; au rattrapage : au moins une matière composée. "
        + "Les taux de la session ordinaire sont rapportés aux inscrits ; ceux du rattrapage, aux étudiants "
        + 'concernés (non admis en session ordinaire).');
    }

    // 3. Simulation de rachat
    if (o.inclureRachat && stats.rachat) {
      const r = stats.rachat;
      titreSection(`Simulation de rachat (seuil ${r.seuil.toFixed(2)} / 20)`);
      autoTable(doc, {
        ...baseTable,
        startY: y,
        head: [['Indicateur', 'Valeur']],
        body: [
          ['Ajournés', fmtInt(r.nb_total_ajournes)],
          [`Ajournés dont la moyenne atteint ${r.seuil.toFixed(2)}`, fmtInt(r.nb_eligibles_rachat)],
          ['Part des ajournés concernés', fmtPct(r.taux_rachat_sur_ajournes)],
          ['Validés si le rachat était appliqué', fmtInt(r.nb_admis_apres_rachat)],
          ['Taux de validation projeté (sur les inscrits)', fmtPct(r.taux_admission_apres_rachat)],
        ].map(row => row.map(safeTxt)),
        columnStyles: { 0: { halign: 'left' }, 1: { halign: 'right', cellWidth: 40 } },
        didParseCell: (d) => { if (d.section === 'head' && d.column.index === 0) d.cell.styles.halign = 'left'; },
      });
      y = finTableau() + 3;
      note("Simulation indicative : aucune décision de rachat n'est appliquée par ce document.");
    }

    // 4. Répartition des résultats (deux tableaux côte à côte)
    titreSection('Répartition des résultats');
    const parMention = new Map((stats.distribution_mention || []).map(m => [m.mention, m.count]));
    const mentions = MENTIONS.map(([cle, lib]) => ({ lib, n: parMention.get(cle) ?? 0 }));
    mentions.push({ lib: 'Sans résultat', n: sansRes });
    const tranches = (stats.distribution_moyennes || []).map(t => {
      const [a, b] = t.tranche.split('-');
      return { lib: b === '20' ? `[${a} ; ${b}]` : `[${a} ; ${b}[`, n: t.count };
    });
    const sansMoyenne = Math.max(inscrits - tranches.reduce((s, t) => s + t.n, 0), 0);
    tranches.push({ lib: 'Sans moyenne', n: sansMoyenne });

    const demi = (LARG - 6) / 2;
    const tableRepartition = (titre: string, lignes: Array<{ lib: string; n: number }>, xGauche: number) => {
      const parts = repartir(lignes.map(l => l.n), inscrits);
      const maxN = Math.max(...lignes.map(l => l.n), 1);
      autoTable(doc, {
        ...baseTable,
        startY: y,
        margin: { left: xGauche, right: W - xGauche - demi, top: HAUT_SUITE, bottom: H - BAS },
        head: [[titre, 'Effectif', 'Part', '']],
        body: lignes.map((l, i) => [safeTxt(l.lib), fmtInt(l.n), safeTxt(parts[i]), '']),
        columnStyles: {
          0: { halign: 'left' }, 1: { halign: 'right', cellWidth: 16 },
          2: { halign: 'right', cellWidth: 16 }, 3: { cellWidth: 18 },
        },
        didParseCell: (d) => { if (d.section === 'head' && d.column.index === 0) d.cell.styles.halign = 'left'; },
        didDrawCell: (d) => {
          if (d.section !== 'body' || d.column.index !== 3) return;
          const n = lignes[d.row.index]?.n ?? 0;
          if (n <= 0) return;
          const largeur = Math.max(0.5, (n / maxN) * (d.cell.width - 3));
          doc.setFillColor(...BARRE);
          doc.rect(d.cell.x + 1.5, d.cell.y + d.cell.height / 2 - 1.1, largeur, 2.2, 'F');
        },
      });
      return finTableau();
    };
    assurer(50);
    const yDebut = y;
    const finGauche = tableRepartition('Mention', mentions, MX);
    y = yDebut;
    const finDroite = tableRepartition('Moyenne sur 20', tranches, MX + demi + 6);
    y = Math.max(finGauche, finDroite) + 3;
    note("Parts calculées sur l'effectif inscrit. « Sans moyenne » regroupe les étudiants sans résultat "
      + 'et ceux dont la moyenne est vide.');

    // 5. Classes au meilleur taux de validation
    const top = stats.par_classe_top || [];
    if (top.length > 0) {
      titreSection('Classes au meilleur taux de validation (au moins 5 inscrits)');
      autoTable(doc, {
        ...baseTable,
        startY: y,
        head: [['Classe', 'Inscrits', 'Validés', 'Ajournés', 'Sans résultat', 'Taux de validation', 'Moyenne']],
        body: top.map(c => {
          const sr2 = c.sans_resultat ?? Math.max(c.inscrits - c.admis_total - c.ajournes, 0);
          return [
            safeTxt(c.libelle), fmtInt(c.inscrits), fmtInt(c.admis_total), fmtInt(c.ajournes), fmtInt(sr2),
            safeTxt(pctDe(c.admis_total, c.inscrits)), c.moyenne_generale.toFixed(2),
          ];
        }),
        columnStyles: {
          0: { halign: 'left' }, 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' },
          4: { halign: 'right' }, 5: { halign: 'right', fontStyle: 'bold' }, 6: { halign: 'right' },
        },
        didParseCell: (d) => { if (d.section === 'head' && d.column.index === 0) d.cell.styles.halign = 'left'; },
      });
      y = finTableau() + 5;
    }

    // 6+. Détails par dimension
    const tableauDetail = (titre: string, premiereCol: string, items: BreakdownDeliberation[] | undefined) => {
      if (!items || items.length === 0) return;
      titreSection(titre);
      let tIns = 0, tPres = 0, tSO = 0, tSR = 0, tAj = 0, tSans = 0, t14 = 0, t16 = 0;
      const body = items.map(g => {
        const sans = g.sans_resultat ?? Math.max(g.inscrits - g.admis_ord - g.admis_ratt - g.ajournes, 0);
        const nonCalcule = g.inscrits > 0 && sans === g.inscrits;
        tIns += g.inscrits; tPres += g.participants_ord; tSO += g.admis_ord; tSR += g.admis_ratt;
        tAj += g.ajournes; tSans += sans; t14 += g.sup14; t16 += g.sup16;
        return [
          safeTxt(g.libelle), fmtInt(g.inscrits), fmtInt(g.participants_ord),
          fmtInt(g.admis_ord), fmtInt(g.admis_ratt), fmtInt(g.ajournes), fmtInt(sans),
          nonCalcule ? 'n.c.' : safeTxt(pctDe(g.admis_ord + g.admis_ratt, g.inscrits)),
          nonCalcule ? '-' : g.moyenne_generale.toFixed(2),
          fmtInt(g.sup14), fmtInt(g.sup16),
        ];
      });
      const NUM = { halign: 'right' as const, cellWidth: 12.2 };
      autoTable(doc, {
        ...baseTable,
        startY: y,
        styles: { ...baseTable.styles, fontSize: 7.2, cellPadding: 1.2 },
        head: [[premiereCol, 'Inscrits', 'Présents', 'Admis SO', 'Admis SR', 'Ajournés', 'Sans rés.', 'Validation', 'Moyenne', 'Bien et +', 'Très bien'].map(safeTxt)],
        body,
        foot: [[
          'Ensemble', fmtInt(tIns), fmtInt(tPres), fmtInt(tSO), fmtInt(tSR), fmtInt(tAj), fmtInt(tSans),
          safeTxt(pctDe(tSO + tSR, tIns)), (stats.moyenne_generale ?? 0).toFixed(2), fmtInt(t14), fmtInt(t16),
        ]],
        showFoot: 'lastPage',
        columnStyles: {
          0: { halign: 'left' }, 1: NUM, 2: NUM, 3: NUM, 4: NUM, 5: NUM, 6: NUM,
          7: { ...NUM, cellWidth: 14, fontStyle: 'bold' }, 8: NUM, 9: NUM, 10: NUM,
        },
        didParseCell: (d) => {
          if (d.section === 'head') d.cell.styles.halign = d.column.index === 0 ? 'left' : 'center';
          if (d.section === 'foot') d.cell.styles.halign = d.column.index === 0 ? 'left' : 'right';
        },
      });
      y = finTableau() + 5;
    };
    tableauDetail('Détail par niveau', 'Niveau', stats.par_niveau);
    tableauDetail('Détail par pôle', 'Pôle', stats.par_pole);
    tableauDetail('Détail par option', 'Option', stats.par_option);
    tableauDetail('Détail par niveau et pôle', 'Niveau · Pôle', stats.par_niveau_pole);
    tableauDetail('Détail par niveau et option', 'Niveau · Option', stats.par_niveau_option);
    note('SO : session ordinaire · SR : session de rattrapage · Validation : validés (SO + SR) rapportés aux '
      + 'inscrits · n.c. : semestre non calculé pour tout le groupe.');
  }

  // ─── Lieu, date et visa ───────────────────────────────────────────────────
  assurer(36);
  y += 4;
  const ville = o.institut?.ville;
  texte(ville ? `Fait à ${ville}, le ${dateFr}` : `Fait le ${dateFr}`, W - MX, y, { taille: 9, align: 'right' });
  if (o.institut?.signataireTitre) {
    texte(o.institut.signataireTitre, W - MX, y + 6, { taille: 9, gras: true, align: 'right' });
  }
  if (o.institut?.signataireNom) {
    texte(o.institut.signataireNom, W - MX, y + 26, { taille: 9, gras: true, align: 'right' });
  }

  // ─── En-tête courant et pied de page ──────────────────────────────────────
  const totalPages = doc.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    if (p > 1) {
      texte('INSTITUT INTERNATIONAL 2i', MX, 12, { taille: 7.5, gras: true, couleur: GRIS });
      texte(`Statistiques de délibération${annee ? ` · ${annee}` : ''}`, W - MX, 12, { taille: 7.5, couleur: GRIS, align: 'right' });
      filet(14.5, 0.2);
    }
    filet(H - 14, 0.2);
    texte('Institut International 2i · Instruire pour Mieux Servir', MX, H - 9, { taille: 7.5, couleur: GRIS });
    texte(`Page ${p} sur ${totalPages}`, W - MX, H - 9, { taille: 7.5, couleur: GRIS, align: 'right' });
  }

  doc.save(o.nomFichier);
}
